import { useQuery } from '@tanstack/react-query';
import keyBy from 'lodash/keyBy';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryParam, StringParam } from 'use-query-params';

import { getVenueInventory } from 'apis/inventory-management-service/api';
import { getItemConfigurations } from 'apis/picker-api/api';
import { ItemConfiguration } from 'apis/picker-api/types';
import { getMenuItemsCommissions } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category, Item, MonetaryAmount, Option } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined } from 'common/utils';
import { localize } from 'common/utils';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';
import { WOLT_SPECIALS_CATEGORY_ID } from 'modules/menu/constants';
import { MenuEditorElement, MenuEditorItem } from 'modules/menu/types';

import { MenuLocaleContext } from '../MenuLocaleContext';
import { MenuMetaContext } from '../MenuMetaContext';
import { reportOptionAdd } from '../behavioral';
import { MenuFeaturesContext } from '../components/MenuFeaturesContext';
import { getItemCategory } from '../items/utils';
import { buildCreateOptionFormData } from '../options/utils';
import useMenuData from '../useMenuData';
import { flattenCategories, getParentCategory, isMenuEditorItem, isMenuEditorOption } from '../utils';

type ListContentType = 'all-items' | 'all-options' | 'all-disabled' | 'items-by-category' | 'items-by-child-category';

const convertMonetaryAmountToLocaleString = ({ amount, currency }: MonetaryAmount) =>
  parseFloat(amount).toLocaleString(undefined, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
  });

const buildItemFilter = (filter: string, locale: string) => (item: Item) => {
  const query = filter.trim().toLocaleLowerCase();
  const name = localize(item.name, locale).toLocaleLowerCase();
  const description = localize(item.description, locale).toLocaleLowerCase();
  const originalPrice = item.discounted
    ? convertMonetaryAmountToLocaleString(item.discounted.original_price)
    : undefined;
  const baseprice = convertMonetaryAmountToLocaleString(item.baseprice);
  const gtin = item.barcode_gtin;
  const sku = item.merchant_sku?.toLocaleLowerCase();
  const posId = item.external_id;

  return (
    name.includes(query) ||
    description.includes(query) ||
    originalPrice?.includes(query) ||
    baseprice.includes(query) ||
    gtin?.includes(query) ||
    sku?.includes(query) ||
    posId?.includes(query)
  );
};

const buildOptionFilter = (filter: string, locale: string, items: Item[]) => (option: Option) => {
  const query = filter.trim().toLocaleLowerCase();
  const name = localize(option.name, locale).toLocaleLowerCase();
  const valueNames = option.values.map(value => localize(value.name, locale).toLocaleLowerCase());
  const linkedItemNames = items
    .filter(item => item.option_bindings.find(ob => ob.option_id === option.id))
    .map(item => localize(item.name, locale).toLocaleLowerCase());
  return (
    name.includes(query) ||
    valueNames.some(valueName => valueName.includes(query)) ||
    linkedItemNames.some(itemName => itemName.includes(query))
  );
};

const getItemStatesForCategory = (items: Item[], category: Category) =>
  category.item_bindings.map(binding => items.find(item => item.id === binding.item_id)).filter(isDefined);

const getItemStatesWithNoCategory = (items: Item[], categories: Category[]) =>
  items.filter(item => !getItemCategory(categories, item.id));

const buildItemsList = (
  items: Item[],
  category: Category | null,
  headerType?: 'category-subheader' | 'subcategory-subheader',
  parentCategory?: Category,
): MenuEditorElement[] => {
  if (items.length === 0) {
    return [];
  }
  const itemsList = items.map(item => ({ type: 'item' as const, itemState: item, category }));

  return headerType
    ? [
        {
          type: headerType,
          category,
          parentCategory,
        },
        ...itemsList,
      ]
    : itemsList;
};

const buildOptionsList = (optionStates: Option[], header?: string): MenuEditorElement[] => {
  if (optionStates.length === 0) {
    return [];
  }
  const optionsList = optionStates.map(optionState => ({ type: 'option' as const, optionState }));
  return header ? [{ type: 'options-subheader', name: header }, ...optionsList] : optionsList;
};

const useMenuEditorLists = (
  listContentType: ListContentType,
  settings?: {
    showWoltSpecials?: boolean;
    categoryId?: string;
  },
) => {
  const { country } = useContext(MenuLocaleContext);
  const { showWoltSpecials, categoryId } = settings || {
    showWoltSpecials: false,
    categoryId: undefined,
  };
  const { categories: allCategories, items: allItems, options: allOptions, updateMenuData } = useMenuData();

  const intl = useIntl();

  const { menuId, venueId } = useContext(MenuMetaContext);
  const { primaryLanguage } = useContext(MenuLocaleContext);
  const { customCommissionRead } = useContext(MenuPermissionsContext);
  const { isRetail } = useContext(MenuFeaturesContext);

  const [activeItemId, setActiveItemId] = useQueryParam('item', StringParam);
  const [activeOptionId, setActiveOptionId] = useQueryParam('option', StringParam);
  const [searchFilter, setSearchFilter] = useState('');
  const [itemElementToCopy, setItemElementToCopy] = useState<MenuEditorItem>();
  const [optionToCopy, setOptionToCopy] = useState<Option>();
  const [itemConfigs, setItemConfigs] = useState<Record<string, ItemConfiguration>>({});

  const { data: itemInventory, refetch: refetchInventory } = useQuery(
    [`inventory-management/${venueId}`],
    () => getVenueInventory(venueId),
    { refetchInterval: 30000 },
  );

  const { refetch: refetchItemConfigs } = useQuery(
    ['picker-api/item-configurations'],
    () => getItemConfigurations(menuId),
    {
      enabled: isRetail,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: itemConfigurations => {
        const objectifiedItemConfigs = keyBy(itemConfigurations, 'itemId');
        setItemConfigs(objectifiedItemConfigs);
      },
    },
  );

  const { data: itemCommissions, refetch: refetchItemCommissions } = useQuery(
    [`menus/${menuId}/custom-item-commissions`],
    () => getMenuItemsCommissions(menuId),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: customCommissionRead,
    },
  );

  const updateItemConfig = (updatedItemConfig: Record<string, ItemConfiguration>) => {
    setItemConfigs({ ...itemConfigs, ...updatedItemConfig });
  };

  const categories = useMemo(
    () =>
      showWoltSpecials
        ? [...allCategories]
        : [...allCategories].filter(category => category.id !== WOLT_SPECIALS_CATEGORY_ID),
    [allCategories, showWoltSpecials],
  );

  const itemFilterFn = useMemo(() => buildItemFilter(searchFilter, primaryLanguage), [searchFilter, primaryLanguage]);

  const optionFilterFn = useMemo(
    () => buildOptionFilter(searchFilter, primaryLanguage, [...allItems]),
    [searchFilter, primaryLanguage, allItems],
  );

  const noCategoryItems = useMemo(() => {
    const itemStatesWithoutCategory = getItemStatesWithNoCategory(allItems, allCategories).filter(itemFilterFn);
    return [...buildItemsList(itemStatesWithoutCategory, null, 'category-subheader')];
  }, [itemFilterFn, allItems, allCategories]);

  const menuEditorList = useMemo<MenuEditorElement[]>(() => {
    switch (listContentType) {
      case 'all-items': {
        const categoryItems = flattenCategories(categories).reduce<MenuEditorElement[]>((acc, category) => {
          const itemStates = getItemStatesForCategory(allItems, category).filter(itemFilterFn);
          return [
            ...acc,
            ...buildItemsList(
              itemStates,
              category,
              'category-subheader',
              getParentCategory(category.id, categories) ?? undefined,
            ),
          ];
        }, []);
        return [...categoryItems, ...noCategoryItems];
      }

      case 'all-options': {
        const filteredOptions = allOptions.filter(optionFilterFn);
        return buildOptionsList(filteredOptions);
      }

      case 'all-disabled': {
        const flattenedAvailableCategories = flattenCategories(categories);

        const categoryItems = flattenedAvailableCategories.reduce<MenuEditorElement[]>((acc, category) => {
          const itemStates = getItemStatesForCategory(allItems, category)
            .filter(itemFilterFn)
            .filter(item => !item.enabled);
          return [
            ...acc,
            ...buildItemsList(
              itemStates,
              category,
              'category-subheader',
              getParentCategory(category.id, categories) ?? undefined,
            ),
          ];
        }, []);
        const itemStatesWithoutCategory = getItemStatesWithNoCategory(allItems, allCategories)
          .filter(itemFilterFn)
          .filter(item => !item.enabled);
        const noCategoryItems = [...buildItemsList(itemStatesWithoutCategory, null, 'category-subheader')];
        const options = allOptions
          .filter(optionFilterFn)
          .filter(option => option.values.find(optionValue => !optionValue.enabled));
        const optionsList = buildOptionsList(options, intl.formatMessage({ id: 'menu.optionsWithDisabledChoices' }));
        return [...categoryItems, ...noCategoryItems, ...optionsList];
      }

      case 'items-by-category': {
        const category = allCategories.find(category => category.id === categoryId) || null;

        if (!category?.child_categories.length) {
          const itemStates = category
            ? getItemStatesForCategory(allItems, category)
            : getItemStatesWithNoCategory(allItems, allCategories);
          const filteredItems = itemStates.filter(itemFilterFn);
          return buildItemsList(filteredItems, category);
        }

        const categoryItems = category.child_categories.reduce<MenuEditorElement[]>((acc, childCategory) => {
          const itemStates = getItemStatesForCategory(allItems, childCategory).filter(itemFilterFn);
          const itemsList = buildItemsList(itemStates, childCategory, 'subcategory-subheader');
          if (!!itemsList.length) {
            return [...acc, ...itemsList];
          }
          return [
            ...acc,
            {
              type: 'subcategory-subheader',
              name: localize(childCategory.name, primaryLanguage),
              category: childCategory,
              isSubcategory: true,
            },
          ];
        }, []);

        return categoryItems;
      }

      case 'items-by-child-category': {
        const category = flattenCategories(allCategories).find(category => category.id === categoryId) || null;

        const itemStates = category
          ? getItemStatesForCategory(allItems, category)
          : getItemStatesWithNoCategory(allItems, allCategories);
        const filteredItems = itemStates.filter(itemFilterFn);
        return buildItemsList(filteredItems, category);
      }
    }
  }, [
    listContentType,
    categories,
    allItems,
    allCategories,
    allOptions,
    itemFilterFn,
    noCategoryItems,
    primaryLanguage,
    optionFilterFn,
    intl,
    categoryId,
  ]);

  const activeItem = useMemo(
    () => (activeItemId ? allItems.find(item => item.id === activeItemId) : undefined),
    [activeItemId, allItems],
  );

  const activeOption = useMemo(
    () => allOptions.find(option => option.id === activeOptionId),
    [activeOptionId, allOptions],
  );

  const handleItemCopy = useCallback(
    (itemId: string, isSpecial: boolean) => {
      const itemElementToCopy = menuEditorList.filter(isMenuEditorItem).find(item => item.itemState.id === itemId);
      setItemElementToCopy(itemElementToCopy);
      setActiveItemId(
        isSpecial || itemElementToCopy?.category?.id === WOLT_SPECIALS_CATEGORY_ID ? 'new-special' : 'new-item',
      );
    },
    [menuEditorList, setActiveItemId],
  );

  const handleOptionCopy = useCallback(
    (optionId: string) => {
      const optionElementToCopy = menuEditorList
        .filter(isMenuEditorOption)
        .find(option => option.optionState.id === optionId);
      setOptionToCopy(optionElementToCopy?.optionState);
      setActiveOptionId('new-option');
    },
    [menuEditorList, setActiveOptionId],
  );

  const newOptionModalProps = useMemo(
    () => ({
      country,
      menuId,
      initialFormValues: buildCreateOptionFormData(primaryLanguage, optionToCopy),
      onOptionCreated: (option: Option) => {
        updateMenuData({ actionType: 'addOption', payload: option });
        reportOptionAdd();
        setActiveOptionId(option.id);
        setOptionToCopy(undefined);
      },
      primaryLanguage,
      isOpen: activeOptionId === 'new-option',
      onClose: () => {
        setActiveOptionId(prevId => (!prevId || prevId === 'new-option' ? undefined : prevId));
        setOptionToCopy(undefined);
      },
    }),
    [activeOptionId, country, menuId, primaryLanguage, optionToCopy, setActiveOptionId, updateMenuData],
  );

  const newItemSidePanelProps = useMemo(
    () => ({
      isOpen: activeItemId === 'new-item' || activeItemId === 'new-special',
      itemToCopy: itemElementToCopy?.itemState,
      initialCategoryId:
        activeItemId === 'new-special'
          ? WOLT_SPECIALS_CATEGORY_ID
          : itemElementToCopy?.category?.id || categoryId || null,
      onSuccess: (itemId: string) => {
        refetchInventory();
        setActiveItemId(itemId);
        setItemElementToCopy(undefined);
      },
      onClose: () => {
        setActiveItemId(undefined);
        setItemElementToCopy(undefined);
      },
    }),
    [
      activeItemId,
      categoryId,
      itemElementToCopy?.category?.id,
      itemElementToCopy?.itemState,
      refetchInventory,
      setActiveItemId,
    ],
  );

  return {
    menuEditorList,
    activeItem,
    activeOption,
    searchFilter,
    itemCommissions,
    itemInventory,
    itemConfigs,
    newItemSidePanelProps,
    newOptionModalProps,
    setActiveItemId,
    setActiveOptionId,
    setSearchFilter,
    handleItemCopy,
    handleOptionCopy,
    refetchInventory,
    updateItemConfig,
    refetchItemConfigs,
    refetchItemCommissions,
  };
};

export default useMenuEditorLists;
